import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import API from "../../../utils/axios";
import { setDrivers } from "../../../store/actions/project";
import { setSolution } from "../../../store/actions/solution";

import { useSnackbar } from "notistack";
import { compare, exportToXlsx } from "../../../utils/helpers";
import { makeStyles, Tooltip } from "@material-ui/core";
import CommonMaterialTable from "../commonTable/CommonMaterialTable";
import TitleWithEducationalMaterials from "../../common/TitleWithEducationalMaterials";

const useStyles = makeStyles(theme => ({
  tooltip: {
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontSize: "0.8125rem",
    lineHeight: "1.1875rem",
    fontWeight: 400,
    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.24)",
    padding: theme.spacing(1.5),
    maxWidth: "216px"
  },
  tagTooltip: {
    boxShadow:
      "0px 4px 12px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.16)",
    borderRadius: "2px",
    maxWidth: "600px",
    padding: theme.spacing(1),
    backgroundColor: "#5F6368",
    color: "#fff",
    "& span": {
      display: "inline-block",
      backgroundColor: "rgba(255,255,255,.24)",
      fontSize: "1rem",
      lineHeight: "1.1875rem",
      padding: theme.spacing(0.5),
      margin: theme.spacing(0.5)
    }
  }
}));

function DriversOverviewDialog({
  open,
  drivers,
  setDrivers,
  setCurrentDriver,
  handleClose,
  driversListLoaded,
  setCurrentAction,
  solution,
  setSolution,
  editingOutsideId
}) {
  // Open edit panel for driver
  const handleEdit = driver => {
    setCurrentDriver(driver);
    setCurrentAction("edit");
  };

  const classes = useStyles();

  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    id: null
  });

  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (editingOutsideId) {
      setCurrentDriver(
        drivers.find(driver => {
          return driver.id === editingOutsideId;
        })
      );
      setCurrentAction("edit");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingOutsideId]);

  /** Deletes driver by id
   * @param {string} id driver's id
   */
  const deleteDriver = driver => {
    const { id, first_name, last_name } = driver;

    setLoading(true);
    API.delete(`/v1/drivers/${id}`)
      .then(res => {
        console.log(res);

        setDrivers(drivers.filter(el => el.id !== id));

        // update selected drivers
        if (selected.includes(id)) {
          setSelected(selected.filter(el => el !== id));
        }

        if (solution) {
          const newSolution = { ...solution };
          newSolution.routes = newSolution.routes.map(route => {
            return route.driver && route.driver.id === id
              ? { ...route, driver: null }
              : route;
          });

          setSolution(newSolution);
        }
        enqueueSnackbar(`Driver "${first_name} ${last_name}" deleted.`);
      })
      .catch(error => {
        setLoading(false);
        console.log(error);
        enqueueSnackbar("Something went wrong, couldn't delete driver");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      setSelected([]);
    }
  }, [open]);

  const sortDriversByName = drivers => {
    return [...drivers].sort((driver1, driver2) => {
      const compareValue = compare(driver1.first_name, driver2.first_name);
      return compareValue === 0
        ? compare(driver1.last_name, driver2.last_name)
        : compareValue;
    });
  };

  const handleAdd = e => {
    setCurrentDriver(null);
    setCurrentAction("edit");
  };

  const handleDelete = driver => {
    setConfirmDialogState({
      open: true,
      id: driver.id,
      title: `Delete driver?`,
      body: `Are you sure you want to delete this driver? This driver will be deleted from all ongoing and future projects.`,
      action: () => {
        deleteDriver(driver);
      }
    });
  };

  const handleMultipleDelete = () => {
    // TO2DO: need endpoint
    setConfirmDialogState({
      open: true,

      title: `Delete driver${selected.length > 1 ? "s" : ""}?`,
      body: `Are you sure you want to delete ${selected.length} driver${
        selected.length > 1 ? "s" : ""
      }? ${selected.length > 1 ? "These" : "This"} driver${
        selected.length > 1 ? "s" : ""
      } will be deleted from all ongoing and future projects.`,
      action: () => deleteDrivers(selected)
    });
  };

  const deleteDrivers = ids => {
    setLoading(true);

    let driver;
    if (ids.length === 1) {
      driver = drivers.find(item => item.id === ids[0]);
    }

    API.delete(`/v1/drivers/batch`, { data: ids })
      .then(res => {
        console.log(res);

        setDrivers(drivers.filter(el => !ids.includes(el.id)));

        // update selected drivers
        setSelected([]);

        if (solution) {
          const newSolution = { ...solution };
          newSolution.routes = newSolution.routes.map(route => {
            return route.driver && ids.includes(route.driver.id)
              ? { ...route, driver: null }
              : route;
          });

          setSolution(newSolution);
        }
        enqueueSnackbar(
          !driver
            ? `Selected drivers deleted.`
            : `Driver "${driver.first_name} ${driver.last_name}" deleted.`
        );
      })
      .catch(error => {
        console.log(error);
        enqueueSnackbar("Something went wrong, couldn't delete driver");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleMultipleDownload = () => {
    exportToXlsx("selected_drivers.xlsx", [
      ["first_name", "last_name", "email", "mobile_number", "driver_tags"],
      ...sortDriversByName(drivers)
        .filter(driver => selected.includes(driver.id))
        .map(driver => [
          driver.first_name,
          driver.last_name,
          driver.email,
          driver.mobile_number,
          driver.tags.map(el => el.name).join("; ")
        ])
    ]);
  };

  return (
    <>
      <CommonMaterialTable
        confirmDialogState={confirmDialogState}
        setConfirmDialogState={setConfirmDialogState}
        handleClose={handleClose}
        selected={selected}
        data={loading ? [] : sortDriversByName(drivers)}
        title={
          <TitleWithEducationalMaterials
            title={"Drivers"}
            configId={"drivers"}
            sectionName={"drivers"}
            showArticle
            showDivider
          />
        }
        singular="driver"
        plural="drivers"
        upload={{
          enabled: true
        }}
        downloadable
        handleMultipleDownload={handleMultipleDownload}
        handleDelete={handleDelete}
        handleMultipleDelete={handleMultipleDelete}
        setSelected={setSelected}
        loading={!driversListLoaded || loading}
        handleAdd={handleAdd}
        handleEdit={handleEdit}
        open={open}
        columns={[
          {
            title: "Name",
            field: "name",
            editable: "never",
            cellStyle: {
              width: "134px"
            },
            headerStyle: {
              width: "134px"
            },
            customSort: (a, b) => {
              const aName = a.first_name + " " + a.last_name;
              const bName = b.first_name + " " + b.last_name;
              return compare(aName, bName);
            },
            customFilterAndSearch: (val, rowData) => {
              const name = rowData.first_name + " " + rowData.last_name;
              return (name || "").toLowerCase().includes(val.toLowerCase());
            },
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.first_name + " " + rowData.last_name}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.first_name + " " + rowData.last_name}</span>
                </Tooltip>
              );
            }
          },
          {
            title: "Email",
            field: "email",
            editable: "never",

            cellStyle: {
              width: "173px"
            },
            headerStyle: {
              width: "173px"
            },
            searchable: false,
            render: rowData => {
              return (
                <Tooltip
                  title={rowData.email}
                  placement="bottom-start"
                  classes={{ tooltip: classes.tooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.email}</span>
                </Tooltip>
              );
            }
          },
          {
            title: "Phone",
            field: "mobile_number",
            editable: "never",

            cellStyle: {
              width: "110px"
            },
            headerStyle: {
              width: "110px"
            },
            // customFilterAndSearch: (val, rowData) => {
            //   return (rowData.start_address?.formatted_address || "")
            //     .toLowerCase()
            //     .includes(val.toLowerCase());
            // },
            // customSort: (a, b) =>
            //   sortAddresses(a.start_address, b.start_address),
            searchable: false,
            render: rowData => {
              return <span>{rowData.mobile_number || ""}</span>;
            }
          },

          {
            title: "Tags",
            field: "tags",
            editable: "never",
            searchable: false,
            customSort: (a, b) => +a.tags.length - +b.tags.length,
            render: rowData => {
              return (
                <Tooltip
                  title={
                    rowData.tags.length
                      ? rowData.tags.map(tag => <span>{tag.name}</span>)
                      : ""
                  }
                  placement="bottom-start"
                  classes={{ tooltip: classes.tagTooltip }}
                  enterDelay={1000}
                >
                  <span>{rowData.tags.length}</span>
                </Tooltip>
              );
            }
          }
        ]}
      />
      {/* <ListView
        open={open}
        setCurrentAction={setCurrentAction}
        handleClose={handleClose}
        handleAdd={handleAdd}
        handleEdit={handleEdit}
        data={sortDriversByName(drivers)}
        handleDelete={handleDelete}
        handleMultipleDelete={handleMultipleDelete}
        handleMultipleDownload={handleMultipleDownload}
        label={"driver"}
        selected={selected}
        setSelected={setSelected}
        loaded={driversListLoaded}
        compareFunc={handleFilter}
        CustomIcon={({ ...props }) => (
          <PersonIcon fontSize="small" {...props} />
        )}
        getInfo={getDriverInfo}
        loading={loading}
        setBackFromEditAction={setBackFromEditAction}
        backFromEditAction={backFromEditAction}
      />
      <ConfirmDialog
        open={confirmDialogState.open}
        handleClose={() => setConfirmDialogState({ open: false })}
        action={() => deleteDriver(confirmDialogState.id)}
        title={"Delete driver?"}
        body={
          "Are you sure you want to delete this driver? This driver will be deleted from all ongoing and future projects."
        }
      /> */}
    </>
  );
}

function mapStateToProps(state) {
  return {
    drivers: state.project.drivers || [],
    solution: state.project.solution,
    driversListLoaded: state.loading.driversListLoaded
  };
}

export default connect(mapStateToProps, {
  setDrivers,
  setSolution
})(DriversOverviewDialog);
